/* import __COLOCATED_TEMPLATE__ from './chart-grid-layout.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import type Chart from 'embercom/models/reporting/custom/chart';
import { inject as service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Report from 'embercom/models/reporting/custom/report';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { type Filter } from '../filters';
import { isEmpty } from '@ember/utils';
import generateUUID from 'embercom/lib/uuid-generator';
import type ReportingTemplates from 'embercom/services/reporting-templates';
import { type ChartTemplate } from 'embercom/services/reporting-templates';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type { GridStack } from 'gridstack';
import type IntlService from 'ember-intl/services/intl';
import { handleChartTemplateDragEvent } from 'embercom/lib/reporting/custom/draggable-chart-template-helper';
import type { Aggregation } from 'embercom/objects/reporting/unified/metrics/types';

export const CELL_HEIGHT = 80;

interface ReportState {
  settings: string;
  dateRange: Range;
  filters: Filter;
  timezone: string;
}

interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Args {
  charts: Chart[];
  reportState: ReportState;
  report: Report;
  deleteChart: () => void;
  editChart: () => void;
  duplicateChart: () => void;
  reorderCharts: () => void;
  trackAnalyticsEvent: () => void;
  editMode: boolean;
  paywall: Component;
  actionsAreDisabled?: boolean;
  isStandalone: boolean;
  inPerformancePage: boolean;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
  Blocks: any;
}

export default class ChartGridLayout extends Component<Signature> {
  @service declare reportingChartService: ReportingChartService;
  @service appService: any;
  @service store: any;
  @service declare intercomEventService: any;
  @service declare reportingTemplates: ReportingTemplates;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;

  @tracked showPropagationFinishedBanner = false;
  @tracked width = 0;
  @tracked gridElementHeight = 0;
  gridId: string;
  dragInItem = 'div[add-chart-side-panel] .grid-stack-item';
  cellHeight = `${CELL_HEIGHT}px`;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.gridId = generateUUID(); // we can render multiple grids on the same page, so we need a unique id
  }

  resetColors = modifier(() => {
    this.reportingChartService.resetColors();
  });

  get gridIsStatic() {
    return !this.args.editMode;
  }

  get chartTemplates() {
    return [
      ...this.reportingTemplates.chartTemplates,
      ...this.reportingTemplates.componentTemplates,
    ];
  }

  // we will hide the first banner if we are in the performance page
  @action
  shouldHideBanner(chart: Chart, index: number) {
    return this.args.inPerformancePage && index === 0 && chart.isBanner;
  }

  get minRows() {
    if (!this.args.editMode) {
      return 1;
    }
    // We want to have the minimum rows to fill the screen vertically
    // that way we have a full height area to drag new charts into
    // Setting this too high, causes the grid to be vertically scrollable
    let approxHeight = window.innerHeight - 150;
    let gridHeight = this.gridElementHeight > 0 ? this.gridElementHeight : approxHeight;
    return Math.max(1, Math.floor(gridHeight / CELL_HEIGHT));
  }

  @action
  updateElementHeight(el: Element) {
    this.gridElementHeight = el.clientHeight;
  }

  @action
  updateShowPropagationFinishedBanner() {
    this.showPropagationFinishedBanner = true;
  }

  @action
  reloadPage() {
    window.location.reload();
  }

  @action
  onGridChange(_: any, items: GridItem[]) {
    (items || []).forEach((item) => {
      let chart = this.args.charts.find((chart) => chart.id === item.id);
      if (!chart) {
        return;
      }
      chart.gridPositionX = item.x;
      chart.gridPositionY = item.y;
      chart.gridWidth = item.w;
      chart.gridHeight = item.h;

      if (items.length === 1) {
        this.scrollElementIntoView(chart.id);
      }
    });
  }

  @action
  async onGridAdded(_: any, items: GridItem[], grid: GridStack) {
    let chartId: string;
    if (items[0].id === undefined) {
      chartId = await this.interceptElementId(items[0], grid);
      if (isEmpty(chartId)) {
        return;
      }
    } else {
      chartId = items[0].id;
    }

    let chartToUpdate = this.args.charts.find(({ id }) => id === chartId);
    if (
      chartToUpdate &&
      isEmpty(chartToUpdate.gridPositionX) &&
      isEmpty(chartToUpdate.gridPositionY)
    ) {
      let item = items[0];
      chartToUpdate.updateChartPosition(item.x, item.y);

      this.scrollElementIntoView(chartId);
    }
  }

  @action
  async interceptElementId(item: any, grid: GridStack) {
    let el = item.el;
    let paywalled = el.getAttribute('data-template-is-paywalled');
    if (paywalled === 'true') {
      return;
    }

    let chartTemplateId = el.getAttribute('data-chart-template-id');
    let aggregation = el.getAttribute('data-template-aggregation');
    let selectedOfficeHours = el.getAttribute('data-template-office-hours');
    let template = this.chartTemplates.find(
      (chart: ChartTemplate) => chart.templateId === chartTemplateId,
    );
    let chartDefinition = this.chartDefinition(template!, aggregation, selectedOfficeHours);
    let chart = await this.args.report.addChartFromTemplate(chartDefinition);

    grid.removeWidget(el);
    return chart.id;
  }

  @action
  handleDragEvent(event: Event, gridStack: GridStack) {
    this.setCellWidth(gridStack);
    return handleChartTemplateDragEvent(event, this.width, this.chartTemplates, this.intl);
  }

  @action
  setCellWidth(gridStack: GridStack) {
    this.width = gridStack.cellWidth();
  }

  @action
  chartDefinition(
    chartTemplate: ChartTemplate,
    aggregation: Aggregation,
    selectedOfficeHours: string,
  ) {
    return this.reportingTemplates.buildChartFromTemplate(
      chartTemplate,
      aggregation,
      this.args.report,
      selectedOfficeHours,
    );
  }

  @action
  onChartResized(_: any, el: any) {
    let chart = this.store.peekRecord('reporting/custom/chart', el.gridstackNode.id);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'resized',
      object: 'reporting_chart',
      chartId: el.gridstackNode.id,
      gridWidth: el.gridstackNode.w,
      gridHeight: el.gridstackNode.h,
      visType: chart?.visualizationType,
    });
  }

  @action
  onChartMoved(_: any, el: any) {
    let chart = this.store.peekRecord('reporting/custom/chart', el.gridstackNode.id);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'moved',
      object: 'reporting_chart',
      chartId: el.gridstackNode.id,
      gridX: el.gridstackNode.x,
      gridY: el.gridstackNode.y,
      visType: chart?.visualizationType,
    });
  }

  scrollElementIntoView(chartId: string) {
    let chartElement = document.getElementById(`grid-item-${chartId}`);
    if (!chartElement) {
      return;
    }
    chartElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ChartGridLayout': typeof ChartGridLayout;
    'reporting/custom/report/chart-grid-layout': typeof ChartGridLayout;
  }
}
